import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormElement } from '@/components/FormElement.tsx';
import { InputText } from '@/components/Inputs/InputText.tsx';
import Button from '@/components/core/Button.tsx';
import { IconArrowRight, IconCheckCircle, IconPlus } from '@/icons';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { toast } from '@/hooks/useToast';
import {
  DepartmentAccessLevelDTO,
  getListOrganizationUsersQueryKey,
  useInviteOrganizationUser,
  useListOrganizationUsers,
  useInviteUserToDepartment,
  getGetUsersForDepartmentQueryKey,
  getGetDepartmentUsersQueryKey,
  useGetDepartmentUsers,
  UserDTO,
} from '@/api/trafikportalen';
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AccessLevelDescription, AccessLevelToggle } from './AccessLevelToggle';
import { useAuth } from '@/stores/auth';
import { getListAvailableUserProfilesQueryKey } from '@/api/auth';
import { refreshDepartmentAccess } from '@/stores/auth';
import { RoleIcon } from '../RoleIcon';
import { useRoleIds } from '@/contexts/ConfigContext';
import { DepartmentPicker } from '../DepartmentPicker';
import { SelectedDepartment } from '@/types/selectedDepartment';
import {
  getWriteAccessDepartmentIds,
  isDepartmentUser,
} from '@/helpers/departmentPermissions';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import { trimData } from '@/helpers/trimData';
import { userTypeByAccessLevel } from '@/helpers/accessLevel';
import { isValidEmail } from '@/helpers/validation';
import { useTenantID } from '@/hooks/useTenantID';

const addAdministratorSchema = z
  .object({
    first_name: z.string().min(1),
    last_name: z.string().min(1),
    email: z.string().email(),
    admin_type: z.enum(['organization', 'department']),
    department_ids: z.string().array().optional(),
    access_level: z.enum(['viewer', 'bearer-manager', 'editor']),
  })
  .refine(
    (data) => {
      if (data.admin_type === 'department') {
        return !!data.department_ids && data.department_ids.length > 0;
      }
      return true;
    },
    {
      message:
        'At least one department is required for department administrators',
      path: ['department_ids'],
    },
  );

type AddAdministratorType = z.infer<typeof addAdministratorSchema>;

type FormView = 'details' | 'departmentPicker';

export const AddAdministratorDialog = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { formatMessage } = useIntl();
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const customErrorMap = useCustomErrorMap;
  const queryClient = useQueryClient();
  const inviteOrganizationUser = useInviteOrganizationUser();
  const inviteUserToDepartment = useInviteUserToDepartment();

  const { userProfile } = useAuth();
  const { data: administrators, isLoading: isLoadingOrgAdmins } =
    useListOrganizationUsers();
  const { data: allDepartmentUsers } = useGetDepartmentUsers();

  const [currentView, setCurrentView] = useState<FormView>('details');
  const [selectedDepartments, setSelectedDepartments] = useState<
    SelectedDepartment[]
  >([]);

  const isOnlyDepartmentAdmin = isDepartmentUser();
  const canCreateOrgAdmin = checkUserPermissions({
    permission: 'organization:editor',
  });

  const methods = useForm<AddAdministratorType>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      admin_type: isOnlyDepartmentAdmin ? 'department' : 'organization',
      department_ids: [],
      access_level: DepartmentAccessLevelDTO.editor,
    },
    resolver: zodResolver(addAdministratorSchema, {
      errorMap: customErrorMap(),
    }),
    mode: 'all',
  });

  const watchEmail = methods.watch('email');
  const watchDepartmentIds = methods.watch('department_ids');

  const isDepartmentAdmin = methods.watch('admin_type') === 'department';

  const writeAccessDepartmentIds = getWriteAccessDepartmentIds();

  const isEmailValid = useMemo(() => isValidEmail(watchEmail), [watchEmail]);

  const departmentsWhereEmailExists = useMemo(() => {
    if (!isEmailValid || !allDepartmentUsers) return [];

    const departmentIds = allDepartmentUsers
      .filter((user) => user.email?.toLowerCase() === watchEmail?.toLowerCase())
      .map((user) => user.department_id);

    return departmentIds;
  }, [isEmailValid, allDepartmentUsers, watchEmail]);

  const existingUser = useMemo(() => {
    if (!isEmailValid) return undefined;

    return (
      administrators?.find(
        (admin) =>
          admin.email?.toLowerCase() === watchEmail?.toLowerCase() &&
          admin.user_profiles.some(
            (profile) =>
              profile.organization_id === userProfile?.organization_id &&
              !profile.expires_at,
          ),
      ) ||
      allDepartmentUsers?.find(
        (user) => user.email?.toLowerCase() === watchEmail?.toLowerCase(),
      )
    );
  }, [
    isEmailValid,
    administrators,
    allDepartmentUsers,
    watchEmail,
    userProfile?.organization_id,
  ]);

  const onSubmit: SubmitHandler<AddAdministratorType> = useCallback(
    async (data) => {
      const submitData = trimData({ ...data, email: data.email.toLowerCase() });
      try {
        if (data.admin_type === 'organization') {
          await inviteOrganizationUser.mutateAsync({
            data: {
              ...submitData,
              user_type: userTypeByAccessLevel[data.access_level],
            },
          });

          await queryClient.invalidateQueries({
            queryKey: getListOrganizationUsersQueryKey(),
          });

          toast.success(
            {
              title: formatMessage(
                {
                  defaultMessage:
                    'Organisationsadministratören {name} lades till',
                },
                { name: `${data.first_name} ${data.last_name}` },
              ),
            },
            { autoClose: 3000 },
          );
        } else if (
          data.admin_type === 'department' &&
          data.department_ids &&
          data.department_ids.length > 0
        ) {
          for (const departmentId of data.department_ids) {
            await inviteUserToDepartment.mutateAsync({
              data: {
                firstname: submitData.first_name,
                lastname: submitData.last_name,
                email: submitData.email,
                department_id: departmentId,
                access_level: data.access_level,
              },
            });
          }

          await Promise.all([
            ...data.department_ids.map((departmentId) =>
              queryClient.invalidateQueries({
                queryKey: getGetUsersForDepartmentQueryKey(departmentId),
              }),
            ),
            queryClient.invalidateQueries({
              queryKey: getGetDepartmentUsersQueryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: getListAvailableUserProfilesQueryKey(),
            }),
          ]);

          await refreshDepartmentAccess();

          toast.success(
            {
              title: formatMessage(
                {
                  defaultMessage:
                    'Avdelningsadministratören {name} lades till i {count} {count, plural, one {avdelning} other {avdelningar}}',
                },
                {
                  name: `${data.first_name} ${data.last_name}`,
                  count: data.department_ids.length,
                },
              ),
            },
            { autoClose: 3000 },
          );
        }

        onClose();
      } catch (error) {
        console.error('Failed to add administrator:', error);
        toast.error(
          {
            title: formatMessage({
              defaultMessage: 'Kunde inte lägga till administratören',
            }),
          },
          { autoClose: 3000 },
        );
      }
    },
    [
      inviteOrganizationUser,
      inviteUserToDepartment,
      onClose,
      queryClient,
      formatMessage,
    ],
  );

  const handleDepartmentSelect = useCallback(
    (department: SelectedDepartment) => {
      setSelectedDepartments((prev) => {
        if (prev.some((d) => d.id === department.id)) {
          return prev.filter((d) => d.id !== department.id);
        }
        return [...prev, department];
      });

      const currentIds = methods.getValues('department_ids') || [];
      if (currentIds.includes(department.id)) {
        methods.setValue(
          'department_ids',
          currentIds.filter((id) => id !== department.id),
          {
            shouldValidate: true,
            shouldDirty: true,
          },
        );
      } else {
        methods.setValue('department_ids', [...currentIds, department.id], {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    [methods],
  );

  const selectedDepartmentIcons = useMemo(() => {
    const icons: Record<string, React.ReactNode> = {};
    (watchDepartmentIds || []).forEach((deptId) => {
      icons[deptId] = <IconCheckCircle className="text-primary-600" />;
    });
    return icons;
  }, [watchDepartmentIds]);

  const isSubmitDisabled = useMemo(() => {
    if (inviteOrganizationUser.isPending || inviteUserToDepartment.isPending) {
      return true;
    }

    if (!isDepartmentAdmin && existingUser) {
      return true;
    }

    if (isLoadingOrgAdmins) {
      return true;
    }

    if (!methods.formState.isDirty) {
      return true;
    }

    if (
      isDepartmentAdmin &&
      (!watchDepartmentIds || watchDepartmentIds.length === 0)
    ) {
      return true;
    }

    if (!methods.formState.isValid) {
      return true;
    }

    return false;
  }, [
    inviteOrganizationUser.isPending,
    inviteUserToDepartment.isPending,
    existingUser,
    isLoadingOrgAdmins,
    methods.formState.isDirty,
    methods.formState.isValid,
    isDepartmentAdmin,
    watchDepartmentIds,
  ]);

  return (
    <FormProvider {...methods}>
      <form ref={ref} onSubmit={methods.handleSubmit(onSubmit)}>
        {currentView === 'details' ? (
          <DetailsForm
            isOnlyDepartmentAdmin={isOnlyDepartmentAdmin}
            canCreateOrgAdmin={canCreateOrgAdmin}
            methods={methods}
            setSelectedDepartments={setSelectedDepartments}
            isSubmitDisabled={isSubmitDisabled}
            isDepartmentAdmin={isDepartmentAdmin}
            isEmailValid={isEmailValid}
            existingUser={existingUser}
            selectedDepartments={selectedDepartments}
            onSelectDepartments={() => setCurrentView('departmentPicker')}
            isLoading={
              inviteOrganizationUser.isPending ||
              inviteUserToDepartment.isPending
            }
          />
        ) : (
          <DepartmentSelection
            onSelect={handleDepartmentSelect}
            nonSelectableDepartments={departmentsWhereEmailExists}
            enabledDepartments={writeAccessDepartmentIds}
            icons={selectedDepartmentIcons}
            onCancel={() => setCurrentView('details')}
          />
        )}
      </form>
    </FormProvider>
  );
};

interface DepartmentSelectionProps {
  onSelect: (department: SelectedDepartment) => void;
  nonSelectableDepartments: string[];
  enabledDepartments: string[];
  icons: Record<string, React.ReactNode>;
  onCancel: () => void;
}

const DepartmentSelection = ({
  onSelect,
  nonSelectableDepartments,
  enabledDepartments,
  icons,
  onCancel,
}: DepartmentSelectionProps) => {
  return (
    <div className="flex flex-col items-center gap-2">
      <DepartmentPicker
        title={<FormattedMessage defaultMessage="Välj avdelningar" />}
        onSelect={onSelect}
        nonSelectableDepartments={nonSelectableDepartments}
        enabledDepartments={enabledDepartments}
        icons={icons}
        dontHighlightSelectedDepartment={true}
      />
      <Button variant="ghost" onClick={onCancel}>
        <FormattedMessage defaultMessage="Klar" />
      </Button>
    </div>
  );
};

interface DetailsFormProps {
  isOnlyDepartmentAdmin: boolean;
  canCreateOrgAdmin: boolean;
  methods: UseFormReturn<AddAdministratorType>;
  setSelectedDepartments: (departments: SelectedDepartment[]) => void;
  isDepartmentAdmin: boolean;
  isEmailValid: boolean;
  existingUser: UserDTO | undefined;
  selectedDepartments: SelectedDepartment[];
  onSelectDepartments: () => void;
  isSubmitDisabled: boolean;
  isLoading: boolean;
}

const DetailsForm = ({
  isOnlyDepartmentAdmin,
  canCreateOrgAdmin,
  methods,
  setSelectedDepartments,
  isDepartmentAdmin,
  isEmailValid,
  existingUser,
  selectedDepartments,
  onSelectDepartments,
  isSubmitDisabled,
  isLoading,
}: DetailsFormProps) => {
  const tenantId = useTenantID();
  const [ref] = useAutoAnimateConditional<HTMLDivElement>();
  const roleIds = useRoleIds();
  const watchEmail = methods.watch('email');
  const watchFirstName = methods.watch('first_name');
  const watchLastName = methods.watch('last_name');
  const watchDepartmentIds = methods.watch('department_ids');
  const watchAdminType = methods.watch('admin_type');
  const watchAccessLevel = methods.watch('access_level');

  const content = useMemo(() => {
    if (!isEmailValid) {
      return null;
    }

    if (existingUser) {
      return (
        <div className="flex flex-col gap-2 rounded-md border border-background-200 p-4">
          <p>
            <FormattedMessage defaultMessage="En administratör med denna e-postadress finns redan i organisationen" />
          </p>
          <Button
            to="/$tenantId/administrators/$administratorId/$view"
            params={{
              tenantId: tenantId,
              administratorId: existingUser.id,
              view: 'details',
            }}
            variant="filled"
            className="w-full justify-between"
            endIcon={<IconArrowRight aria-hidden="true" />}
          >
            <FormattedMessage defaultMessage="Till administratören" />
          </Button>
        </div>
      );
    }

    return (
      <>
        <FormElement
          id="first_name"
          label={<FormattedMessage defaultMessage="Förnamn" />}
          required
          input={
            <InputText
              name="first_name"
              control={methods.control}
              editMode={true}
              error={methods.formState.errors.first_name}
              helperTextReserveSpace
              required
            />
          }
        />
        <FormElement
          id="last_name"
          label={<FormattedMessage defaultMessage="Efternamn" />}
          required
          input={
            <InputText
              name="last_name"
              control={methods.control}
              editMode={true}
              error={methods.formState.errors.last_name}
              helperTextReserveSpace
              required
            />
          }
        />
        {!isOnlyDepartmentAdmin && (
          <div className="flex flex-col gap-2">
            <label className="font-medium text-text-900">
              <FormattedMessage defaultMessage="Typ av administratör" />
            </label>
            <div className="flex flex-wrap gap-2">
              {canCreateOrgAdmin && (
                <Button
                  type="button"
                  variant={
                    watchAdminType === 'organization' ? 'filled' : 'outline'
                  }
                  onClick={() => {
                    methods.setValue('admin_type', 'organization', {
                      shouldValidate: true,
                    });
                    methods.setValue('department_ids', []);
                    setSelectedDepartments([]);
                  }}
                  className="flex-1"
                  startIcon={<RoleIcon roleId={roleIds.orgAdminWrite} />}
                >
                  <FormattedMessage defaultMessage="Organisations­administratör" />
                </Button>
              )}
              <Button
                type="button"
                variant={watchAdminType === 'department' ? 'filled' : 'outline'}
                onClick={() => {
                  methods.setValue('admin_type', 'department', {
                    shouldValidate: true,
                  });
                }}
                className="flex-1"
                startIcon={<RoleIcon roleId={roleIds.deptUser} />}
              >
                <FormattedMessage defaultMessage="Avdelnings­administratör" />
              </Button>
            </div>
          </div>
        )}
        {isDepartmentAdmin && (
          <>
            <FormElement
              label={<FormattedMessage defaultMessage="Avdelningar" />}
              required
              button={
                <Button
                  type="button"
                  className="w-full"
                  onClick={onSelectDepartments}
                >
                  <FormattedMessage
                    defaultMessage="{count, plural, =0 {Välj avdelningar} one {# avdelning vald} other {# avdelningar valda}}"
                    values={{ count: selectedDepartments.length }}
                  />
                </Button>
              }
              input={null}
            />
            {watchFirstName &&
              watchLastName &&
              watchEmail &&
              (!watchDepartmentIds || watchDepartmentIds.length === 0) && (
                <p className="ml-auto text-red-700">
                  <FormattedMessage defaultMessage="Måste välja minst en avdelning" />
                </p>
              )}
          </>
        )}
        <FormElement
          label={
            <span id="access-level-label">
              <FormattedMessage defaultMessage="Rättighet" />
            </span>
          }
          input={
            <div className="flex flex-col gap-2">
              <AccessLevelToggle
                id="access_level"
                labelledBy="access-level-label"
                value={watchAccessLevel}
                onChange={(level) => {
                  if (level !== DepartmentAccessLevelDTO.none) {
                    methods.setValue('access_level', level);
                  }
                }}
              />
              <p className="text-text-700">
                <AccessLevelDescription level={watchAccessLevel} />
              </p>
            </div>
          }
        />
        <p className="mt-2 text-text-800">
          <i>
            <FormattedMessage defaultMessage="Detta sätter bara den initiala informationen. Efter detta är det enbart användaren själv som kan redigera sin information." />
          </i>
        </p>
        <Button
          startIcon={<IconPlus />}
          variant="filled"
          type="submit"
          className="w-full"
          disabled={isSubmitDisabled}
          loading={isLoading}
        >
          <FormattedMessage defaultMessage="Lägg till administratör" />
        </Button>
      </>
    );
  }, [
    canCreateOrgAdmin,
    existingUser,
    isDepartmentAdmin,
    isEmailValid,
    isLoading,
    isOnlyDepartmentAdmin,
    isSubmitDisabled,
    methods,
    onSelectDepartments,
    roleIds,
    selectedDepartments,
    setSelectedDepartments,
    tenantId,
    watchAccessLevel,
    watchAdminType,
    watchDepartmentIds,
    watchEmail,
    watchFirstName,
    watchLastName,
  ]);

  return (
    <div ref={ref} className="flex flex-col gap-2">
      <FormElement
        id="email"
        label={<FormattedMessage defaultMessage="E-postadress" />}
        required
        input={
          <InputText
            name="email"
            control={methods.control}
            editMode={true}
            error={methods.formState.errors.email}
            helperTextReserveSpace
            required
          />
        }
      />
      {content}
    </div>
  );
};
